import React, {useState, useEffect, useRef, useCallback} from 'react';
import {ChevronDown, ChevronUp, Trash2, Loader} from 'lucide-react';
import CatalogSearchBar from "../CatalogSearchBar";
import CategoryEditor from './CategoryEditor';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {URLS_UPLOADS} from "../../../config/url";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import Notification from "../../Common/Notification";

// Swaps two categories in the full, display-ordered list and renumbers every
// position 0..n-1. Categories default to pos_order 0 on the backend, so just
// swapping the two values would often swap 0 with 0 and change nothing.
// Returns only the {id, order} pairs whose pos_order actually changes.
export const buildReorderUpdates = (categories, movedId, targetId) => {
    const ordered = [...categories];
    const from = ordered.findIndex(c => c.id === movedId);
    const to = ordered.findIndex(c => c.id === targetId);
    if (from === -1 || to === -1) return [];
    [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
    return ordered
        .map((c, order) => ({id: c.id, order, previous: c.pos_order}))
        .filter(({order, previous}) => previous !== order)
        .map(({id, order}) => ({id, order}));
};

const Categories = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // ── URL-persisted state ──────────────────────────────────────────
    const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    const debouncedSearchTerm = searchParams.get('q') || '';
    // Filtre de statut : '' (tous) | 'true' (actifs) | 'false' (inactifs)
    const activeFilter = ['true', 'false'].includes(searchParams.get('active')) ? searchParams.get('active') : '';

    // Local input state (debounced before writing to URL)
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');

    // Sync input when URL changes externally (e.g. browser Back)
    useEffect(() => {
        setSearchTerm(searchParams.get('q') || '');
    }, [searchParams]);

    const updateParams = useCallback((updates, push = false) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v === '' || v === null || v === undefined) next.delete(k);
                else next.set(k, String(v));
            });
            return next;
        }, {replace: !push});
    }, [setSearchParams]);

    const setCurrentPage = useCallback((page) => {
        updateParams({page}, true);
    }, [updateParams]);

    const [selectedAll, setSelectedAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [editCategory, setEditCategory] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: 'success', // 'success', 'error', 'loading'
        message: '',
    });
    const queryClient = useQueryClient();
    const selectionBarRef = useRef(null);

    // Add CSS for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideDown {
                from {
                    opacity: 0;
                    transform: translateY(-20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            .selection-bar-appear {
                animation: slideDown 0.3s ease-out forwards;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Debounce: write search to URL after 600 ms, reset page to 1
    useEffect(() => {
        const timer = setTimeout(() => {
            updateParams({q: searchTerm || null, page: null});
        }, 600);
        return () => clearTimeout(timer);
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    // Apply animation when selection changes
    useEffect(() => {
        if (selectedItems.length > 0 && selectionBarRef.current) {
            selectionBarRef.current.classList.remove('selection-bar-appear');
            // This triggers a reflow, allowing the animation to restart
            void selectionBarRef.current.offsetWidth;
            selectionBarRef.current.classList.add('selection-bar-appear');
        }
    }, [selectedItems.length]);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        // Auto hide after 5 seconds for success notifications
        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // Function to fetch categories from the API with server-side pagination and filtering
    const fetchCategories = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build the query string with pagination and name filter
            let url = `/categories?page=${currentPage}`;
            if (debouncedSearchTerm.trim()) {
                url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            }
            if (activeFilter) {
                url += `&active=${activeFilter}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            // Update totalPages based on the pagination structure
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                return response.data.data;
            }

            // Fallback if response structure is different
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des catégories";
            throw new Error(errorMessage);
        }
    };

    // Function to delete categories
    const deleteCategories = async (categoryIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            console.log("Deleting categories:", categoryIds);

            // Delete categories one by one
            const results = await Promise.all(
                categoryIds.map(id =>
                    api.delete(`/categories/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        console.error(`Failed to delete category ${id}:`, err);
                        return {error: err, id};
                    })
                )
            );

            // Log results for debugging
            console.log("Delete results:", results);

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                console.error("Failed deletions:", failures);
                if (failures.length === categoryIds.length) {
                    throw new Error("Impossible de supprimer les catégories sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${categoryIds.length} catégories n'ont pas pu être supprimées`);
                }
            }

            return categoryIds;
        } catch (error) {
            console.error("Delete error:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des catégories";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteCategories,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries(['categories']);
            showNotification('success', 'Catégories supprimées avec succès');
        },
        onError: (error) => {
            console.error("Delete mutation error:", error);
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Use react-query to fetch and cache the data
    const {data: categories, isLoading, error} = useQuery({
        queryKey: ['categories', currentPage, debouncedSearchTerm, activeFilter],
        queryFn: fetchCategories,
        keepPreviousData: true,
    });

    // Use the data directly since filtering happens on the server
    const filteredData = categories || [];

    // Persist the move: reload every category in display order (the server
    // sorts by pos_order, then name), apply the swap and renumber them all
    // (backend: POST /categories/reorder { orders: [{id, order}, ...] }).
    const reorderCategories = async ({movedId, targetId}) => {
        const accountId = await secureStorage.getItem("selectedAccount");
        const headers = {
            'X-account-id': accountId.id,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        };

        const {data} = await api.get('/categories?page=1&itemsPerPage=1000', {headers});
        const all = data?.data || data || [];
        const updates = buildReorderUpdates(all, movedId, targetId);
        if (updates.length > 0) {
            await api.post('/categories/reorder', {orders: updates}, {headers});
        }
    };

    const reorderMutation = useMutation({
        mutationFn: reorderCategories,
        onError: (error) => {
            console.error("Reorder error:", error);
            showNotification('error', "Erreur lors du changement d'ordre");
        },
        // Resync with the server either way (the local swap was optimistic)
        onSettled: () => queryClient.invalidateQueries({queryKey: ['categories']}),
    });
    // React Query v5: mutations expose isPending (isLoading no longer exists)
    const isReordering = reorderMutation.isPending;

    // Reordering needs the full list: off while a search or status filter is applied.
    const isFiltered = !!debouncedSearchTerm.trim() || !!activeFilter;

    // Move a category up/down by swapping it with its neighbour on this page.
    const handleMoveCategory = (index, direction) => {
        if (isFiltered || isReordering) return;

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= filteredData.length) return;

        const current = filteredData[index];
        const swapWith = filteredData[targetIndex];

        // Optimistic local swap so the UI reacts immediately
        queryClient.setQueryData(['categories', currentPage, debouncedSearchTerm, activeFilter], (old) => {
            if (!old) return old;
            const next = [...old];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });

        reorderMutation.mutate({movedId: current.id, targetId: swapWith.id});
    };

    // Generate pagination buttons array
    const pages = Array.from({length: totalPages}, (_, index) => index + 1);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredData.map(item => item.id) : []);
    };

    // Handle individual item selection
    const handleSelectItem = (categoryId, e) => {
        e.stopPropagation();
        setSelectedItems((prevSelected) =>
            prevSelected.includes(categoryId)
                ? prevSelected.filter((id) => id !== categoryId)
                : [...prevSelected, categoryId]
        );
    };

    // Handle deletion of selected categories
    const handleDeleteSelected = () => {
        if (selectedItems.length > 0) {
            setShowConfirmDialog(true);
        }
    };

    // Function to confirm deletion
    const confirmDelete = () => {
        setShowConfirmDialog(false);
        deleteMutation.mutate(selectedItems);
    };

    // Function to cancel deletion
    const cancelDelete = () => {
        setShowConfirmDialog(false);
    };

    // Handle edit category
    const handleEditCategory = (category) => {
        navigate(`/catalogue/edit/${category.id}`, {state: {returnTo: location.pathname + location.search}});
    };

    // Handle back to categories list
    const handleBackToCategories = () => {
        setShowEditor(false);
    };

    // Handle pagination
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    // Show loading state
    if (isLoading) return <div className="flex justify-center items-center h-40">
        <Loader className="w-8 h-8 text-primary animate-spin"/>
    </div>;

    // Show error state
    if (error) return <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>;

    return (
        <div className="max-w-full">
            {/* Categories List */}
            {!showEditor && (
                <div>
                    {/* Search Bar — même style que l'onglet Produits */}
                    <CatalogSearchBar
                        value={searchTerm}
                        onChange={setSearchTerm}
                        activeSearch={debouncedSearchTerm}
                        status={activeFilter}
                        onStatusChange={(value) => updateParams({active: value || null, page: null})}
                        onClearAll={() => {
                            setSearchTerm('');
                            updateParams({q: null, active: null, page: null});
                        }}
                        onClear={() => {
                            setSearchTerm('');
                            updateParams({q: null, page: null});
                        }}
                    />

                    {/* Selected Items Info and Delete Button */}
                    {selectedItems.length > 0 && (
                        <div
                            ref={selectionBarRef}
                            className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20
                                border-l-4 border-primary shadow-sm selection-bar-appear"
                        >
                            <div className="flex items-center">
                                <div className="w-6 h-6 bg-primary flex items-center justify-center rounded-sm mr-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white"
                                         viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </div>
                                <span
                                    className="text-gray-700 dark:text-gray-200">{selectedItems.length} catégorie{selectedItems.length > 1 ? 's' : ''} sélectionnée{selectedItems.length > 1 ? 's' : ''}</span>
                            </div>
                            <button
                                onClick={handleDeleteSelected}
                                className="flex items-center px-3 py-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-colors duration-200"
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="w-5 h-5 mr-1.5"/>
                                <span>Supprimer la sélection</span>
                            </button>
                        </div>
                    )}

                    {/* Table Header */}
                    {/* Table Structure */}
                    <div className="card overflow-x-auto">
                        <table className="w-full table-fixed">
                            <thead>
                            <tr className="bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                                <th className="w-10 px-2 sm:px-4 py-3 text-left">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                        checked={selectedAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    <div className="flex items-center">
                                        NOM
                                        <ChevronDown className="ml-1 w-4 h-4 text-gray-500 dark:text-gray-400"/>
                                    </div>
                                </th>
                                <th className="w-16 sm:w-20 px-1 sm:px-4 py-3 text-center sm:text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Ordre
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="p-4 text-center text-gray-500 dark:text-gray-400">
                                        Aucune catégorie trouvée
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr
                                        key={item.id}
                                        className="border-b last:border-b-0 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                        onClick={() => handleEditCategory(item)}
                                    >
                                        <td className="w-10 px-2 sm:px-4 py-3">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 accent-primary"
                                                checked={selectedItems.includes(item.id)}
                                                onChange={(e) => handleSelectItem(item.id, e)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className="px-2 sm:px-4 py-3">
                                            <div className="flex items-center min-w-0">
                                                <div
                                                    className="h-10 w-10 mr-3 flex-shrink-0 flex items-center justify-center text-white text-xs font-medium rounded"
                                                    style={{backgroundColor: item.color || '#4fb7ad'}}
                                                >
                                                    {item.imageUrl && item.imageUrl !== '' ?
                                                        <img
                                                            src={item.thumbnailUrl || (item.imageUrl.startsWith('http') ? item.imageUrl : `${URLS_UPLOADS}${item.imageUrl}`)}
                                                            alt={item.name}
                                                            className="h-full w-full object-cover rounded"
                                                        /> :
                                                        item.name.slice(0, 2).toUpperCase()
                                                    }
                                                </div>
                                                <span className="font-medium text-gray-800 dark:text-gray-100 truncate flex-1 min-w-0">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="w-16 sm:w-20 px-1 sm:px-4 py-1.5 sm:py-3">
                                            <div
                                                className="flex sm:flex-col items-center justify-center gap-1 sm:gap-0"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleMoveCategory(index, 'up')}
                                                    disabled={index === 0 || isFiltered || isReordering}
                                                    title={isFiltered ? "Indisponible pendant une recherche ou un filtre" : "Monter"}
                                                    aria-label="Monter"
                                                    className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-0 sm:min-h-0 sm:p-0.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 sm:hover:bg-transparent disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-500 disabled:hover:bg-transparent transition-colors"
                                                >
                                                    <ChevronUp className="w-4 h-4 sm:w-4 sm:h-4"/>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleMoveCategory(index, 'down')}
                                                    disabled={index === filteredData.length - 1 || isFiltered || isReordering}
                                                    title={isFiltered ? "Indisponible pendant une recherche ou un filtre" : "Descendre"}
                                                    aria-label="Descendre"
                                                    className="flex items-center justify-center min-w-[32px] min-h-[32px] sm:min-w-0 sm:min-h-0 sm:p-0.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 sm:hover:bg-transparent disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-500 disabled:hover:bg-transparent transition-colors"
                                                >
                                                    <ChevronDown className="w-4 h-4 sm:w-4 sm:h-4"/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination - Responsive */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-6 overflow-x-auto">
                            <div className="flex items-center space-x-1 sm:space-x-2">
                                {/* Previous page button */}
                                <button
                                    onClick={handlePrevPage}
                                    className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                        ${currentPage === 1 ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700' : 'text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                    `}
                                    disabled={currentPage === 1}
                                >
                                    {"<"}
                                </button>

                                {/* Page numbers */}
                                {pages.length > 5 ? (
                                    <>
                                        {/* Always show first page */}
                                        <button
                                            onClick={() => setCurrentPage(1)}
                                            className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                                ${1 === currentPage
                                                ? 'text-white border-primary bg-primary font-medium'
                                                : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                            `}
                                        >
                                            1
                                        </button>

                                        {/* Show ellipsis if not at start */}
                                        {currentPage > 3 && <span className="px-1 text-gray-400">...</span>}

                                        {/* Show current and surrounding pages */}
                                        {pages
                                            .filter(page => Math.abs(page - currentPage) < 2 && page !== 1 && page !== totalPages)
                                            .map(page => (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`hidden sm:flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                                        ${page === currentPage
                                                        ? 'text-white border-primary bg-primary font-medium'
                                                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                                    `}
                                                >
                                                    {page}
                                                </button>
                                            ))
                                        }

                                        {/* Show ellipsis if not at end */}
                                        {currentPage < totalPages - 2 &&
                                            <span className="px-1 text-gray-400">...</span>}

                                        {/* Always show last page */}
                                        <button
                                            onClick={() => setCurrentPage(totalPages)}
                                            className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                                ${totalPages === currentPage
                                                ? 'text-white border-primary bg-primary font-medium'
                                                : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                            `}
                                        >
                                            {totalPages}
                                        </button>
                                    </>
                                ) : (
                                    // Show all pages if 5 or fewer
                                    pages.map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                                ${page === currentPage
                                                ? 'text-white border-primary bg-primary font-medium'
                                                : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                            `}
                                        >
                                            {page}
                                        </button>
                                    ))
                                )}

                                {/* Next page button */}
                                <button
                                    onClick={handleNextPage}
                                    className={`flex items-center justify-center w-8 h-8 border rounded-full text-sm font-medium
                                        ${currentPage === totalPages ? 'text-gray-300 dark:text-gray-600 border-gray-200 dark:border-gray-700' : 'text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}
                                    `}
                                    disabled={currentPage === totalPages}
                                >
                                    {">"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CategoryEditor Component */}
            {showEditor && <CategoryEditor category={editCategory} onClose={handleBackToCategories}/>}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="card shadow-xl p-6 max-w-md w-full m-4 selection-bar-appear">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Confirmation de suppression</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Êtes-vous sûr de vouloir
                            supprimer {selectedItems.length} catégorie{selectedItems.length > 1 ? 's' : ''} ?
                            Cette action est irréversible.
                        </p>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button className="btn-ghost" onClick={cancelDelete}>
                                Annuler
                            </button>
                            <button className="btn-danger" onClick={confirmDelete}>
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />
        </div>
    );
};

export default Categories;
